/**
 * Camada 1 · campo de busca com lista, o combobox da plataforma.
 *
 * MIGRADO de `~/Documents/prontidaovoo/src/componentes/CampoBuscaLista.tsx`, que la nasceu da
 * generalizacao do campo de modelo de aeronave quando o fluxo passou a ter DOIS campos com o
 * mesmo comportamento (fabricante e modelo). A mecanica e uma so, e escrita duas vezes
 * divergiria: o mousedown que escolhe antes do blur, o Enter que so age com realce, o Escape e o
 * clique fora sao quatro detalhes que ninguem reescreve igual na segunda vez.
 *
 * POR QUE ELE MORA EM `packages/ui` E NAO NO APP: pelo ADR 0028, o que nao varia por produto e
 * peca da plataforma. Um combobox nao tem nada de aviacao: o que varia e o que ele BUSCA, e isso
 * entra por `buscar`, que o produto fornece. Deixa-lo no app faria o proximo produto que
 * precisasse de busca copia-lo, e a copia envelhece de um lado so.
 *
 * O QUE MUDOU EM RELACAO A ORIGEM, e cada mudanca tem motivo:
 *   · o ROTULO passa a ser obrigatorio no tipo, como no `Campo` desta casa. Campo com
 *     placeholder no lugar de rotulo perde o nome no instante em que alguem digita.
 *   · as classes soltas do Tailwind viraram os tokens `cx-`, porque cor literal e recusada pelo
 *     lint de fronteira desta plataforma.
 *   · entrou `obrigatorio`, que a origem nao tinha, para o campo casar com o resto dos
 *     formularios daqui: a marca visual e o `required` andam juntos, num argumento so.
 *
 * ESCOLHER DA LISTA NUNCA E OBRIGATORIO POR PADRAO: o texto digitado vale como esta. E contrato
 * herdado da origem, e a razao esta escrita la: catalogo e atalho, e nao trava. Quem precisa da
 * trava usa `exigirEscolha`, e o campo de aerodromo e o unico caso hoje.
 */

import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { t } from '@complexity/i18n';

export interface CampoBuscaProps<T> {
  /** Rotulo do campo. Obrigatorio no tipo, e nao por convencao. */
  readonly rotulo: string;
  readonly valor: string;
  readonly aoMudar: (texto: string) => void;
  readonly aoEscolher: (item: T) => void;
  /** Devolve os itens que casam com o texto. Vem de fora: a peca nao conhece o dominio. */
  readonly buscar: (termo: string) => readonly T[];
  readonly renderizarItem: (item: T, realcado: boolean) => ReactNode;
  readonly chaveDoItem: (item: T) => string;
  readonly obrigatorio?: boolean;
  readonly desabilitado?: boolean;
  readonly placeholder?: string;
  /** Mensagem de erro. Presente, o campo fica invalido e a mensagem e associada a ele. */
  readonly erro?: string;
  /** Texto de apoio abaixo do campo. Escondido quando ha erro, para nao competir com ele. */
  readonly ajuda?: string;
  readonly testid: string;
}

export function CampoBusca<T>({
  rotulo,
  valor,
  aoMudar,
  aoEscolher,
  buscar,
  renderizarItem,
  chaveDoItem,
  obrigatorio = false,
  desabilitado = false,
  placeholder,
  erro,
  ajuda,
  testid,
}: CampoBuscaProps<T>) {
  // useId gera um id estavel entre servidor e cliente. Contador proprio produz id diferente nos
  // dois lados e quebra a associacao rotulo/campo na hidratacao.
  const id = useId();
  const idLista = `${id}-lista`;
  const idErro = `${id}-erro`;
  const idAjuda = `${id}-ajuda`;

  const [aberta, definirAberta] = useState(false);
  // -1 e NENHUM realce, e nao "o primeiro": quem so digita nunca e surpreendido por um Enter
  // escolhendo algo que ele nao apontou.
  const [realce, definirRealce] = useState(-1);
  const raiz = useRef<HTMLDivElement | null>(null);

  const resultados = aberta && !desabilitado ? buscar(valor) : [];

  // mousedown, e NAO click: o click no item dispara DEPOIS do blur do input, e com click aqui a
  // lista fecharia antes de o item receber o clique. E o defeito classico de combobox, e ele so
  // aparece no uso real, nunca na leitura do codigo.
  useEffect(() => {
    function aoClicarFora(evento: MouseEvent) {
      if (raiz.current && !raiz.current.contains(evento.target as Node)) definirAberta(false);
    }
    document.addEventListener('mousedown', aoClicarFora);
    return () => document.removeEventListener('mousedown', aoClicarFora);
  }, []);

  function escolher(item: T) {
    aoEscolher(item);
    definirAberta(false);
    definirRealce(-1);
  }

  return (
    <div className="flex flex-col gap-cx-1">
      <label htmlFor={id} className="text-cx-sm font-medium text-texto">
        {rotulo}
        {obrigatorio && (
          <>
            {/* O asterisco e decorativo: quem le com leitor de tela recebe a palavra inteira
                logo em seguida, porque "asterisco" nao significa nada falado. */}
            <span aria-hidden="true" className="ml-cx-1 text-erro">
              *
            </span>
            {/* PASSA PELO DICIONARIO, e ate 08/09/2026 nao passava. A classe `cx-so-leitor`
                esconde esta frase da tela: quem a recebe e quem usa leitor de tela, e
                por isso o defeito era invisivel. Com a tela inteira em ingles, a pessoa
                ouvia "Email, star, (obrigatório)". */}
            <span className="cx-so-leitor">{t('(obrigatório)')}</span>
          </>
        )}
      </label>

      <div ref={raiz} className="relative">
        <input
          id={id}
          type="text"
          role="combobox"
          aria-expanded={aberta}
          aria-autocomplete="list"
          aria-controls={idLista}
          aria-invalid={erro ? true : undefined}
          aria-describedby={erro ? idErro : ajuda ? idAjuda : undefined}
          // O navegador NAO pode autocompletar por cima: ele desenharia a propria lista em cima
          // desta, e a pessoa escolheria de uma lista que o sistema nao conhece.
          autoComplete="off"
          required={obrigatorio}
          disabled={desabilitado}
          value={valor}
          placeholder={placeholder}
          className="cx-campo"
          data-testid={testid}
          onChange={(evento) => {
            aoMudar(evento.target.value);
            definirAberta(true);
            definirRealce(-1);
          }}
          onFocus={() => definirAberta(true)}
          onKeyDown={(evento) => {
            if (!aberta || resultados.length === 0) return;
            if (evento.key === 'ArrowDown') {
              evento.preventDefault();
              definirRealce((r) => Math.min(r + 1, resultados.length - 1));
            } else if (evento.key === 'ArrowUp') {
              evento.preventDefault();
              definirRealce((r) => Math.max(r - 1, -1));
            } else if (evento.key === 'Enter' && realce >= 0) {
              // So escolhe com item REALCADO pelas setas. Sem esta condicao, o Enter que envia o
              // formulario escolheria o primeiro resultado sem a pessoa ter pedido, e ela so
              // descobriria depois de gravar.
              evento.preventDefault();
              escolher(resultados[realce] as T);
            } else if (evento.key === 'Escape') {
              definirAberta(false);
            }
          }}
        />

        {aberta && resultados.length > 0 ? (
          <ul
            id={idLista}
            role="listbox"
            data-testid={`${testid}-lista`}
            // Absoluta, com teto e rolagem propria: a lista NAO empurra os campos de baixo. Sem
            // isso, o formulario inteiro pula a cada tecla digitada.
            className="absolute z-20 mt-cx-1 max-h-64 w-full min-w-72 overflow-y-auto rounded-cx border border-borda bg-superficie shadow-lg"
          >
            {resultados.map((item, indice) => (
              <li key={chaveDoItem(item)} role="option" aria-selected={indice === realce}>
                <button
                  type="button"
                  // min-h-11 sao 44px, o alvo de toque minimo desta casa. Item de lista com
                  // altura de texto e alcancavel com o mouse e nao com o dedo.
                  className={`flex min-h-11 w-full items-center justify-between gap-cx-2 px-cx-3 text-left text-cx-sm ${
                    // ESTA LINHA NAO PINTAVA NADA, e o defeito foi MEDIDO no navegador em
                    // 09/09/2026: `bg-acento-suave` e `text-acento` nao existem em
                    // `packages/tokens/src/tokens.css` nem no preset do Tailwind. O fundo saia
                    // `rgba(0, 0, 0, 0)` e a cor caia para a herdada, `rgb(23, 23, 23)`.
                    //
                    // O QUE ISSO CUSTAVA: quem percorre o combobox pelo teclado NAO via realce
                    // nenhum. A classe existia no arquivo, entao ler o `.tsx` dizia que o
                    // realce estava resolvido, e so a cor computada no navegador mostrou que
                    // nao. Classe inexistente nao quebra o build e nao acende nada.
                    //
                    // O PAR ESCOLHIDO E O MESMO DO `Selecao`, de proposito: sao duas listas
                    // que a pessoa percorre do mesmo jeito, e realce diferente em cada uma
                    // faria a plataforma parecer dois produtos.
                    indice === realce ? 'bg-marca/15 dark:bg-marca/25 text-texto' : 'text-texto'
                  }`}
                  onMouseDown={(evento) => {
                    evento.preventDefault();
                    escolher(item);
                  }}
                  onMouseEnter={() => definirRealce(indice)}
                >
                  {renderizarItem(item, indice === realce)}
                </button>
              </li>
            ))}
          </ul>
        ) : null}
      </div>

      {erro && (
        // role="alert" faz o leitor de tela anunciar assim que a mensagem aparece, em vez de so
        // ao chegar no campo, que seria tarde para quem ja tentou enviar.
        <p id={idErro} role="alert" className="text-cx-sm text-erro">
          {erro}
        </p>
      )}
      {!erro && ajuda && (
        <p id={idAjuda} className="text-cx-sm text-texto-suave">
          {ajuda}
        </p>
      )}
    </div>
  );
}
